import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { isEmpty } from 'lodash';
import {
  ApplicationName,
  FormNavigation,
  StepContainer
} from 'components/CASAForm';
import {
  AuSelect,
  AuDirectionLink,
  AuDateInput
} from 'components/DTA';
import { Loader } from 'components/common';
import { FormContext } from 'contexts';
import { DampReportContext } from 'contexts';
import { useFormikContext } from 'formik';
import { DampReportApplicationFormData } from 'types/forms/dampReportApplicationTypes';
import { DAMP_FORM_VALIDATION_ERROR_ALERT } from 'arnConstants';

const DampReportDetails: React.FC = () => {
  const { values, validateForm, getFieldMeta } = useFormikContext<DampReportApplicationFormData>();
  const { setFormAlert, openStepById, setOnChangeValidation } = useContext(FormContext);
  const { referenceDataResponse } = useContext(DampReportContext);
  const history = useHistory();

  const handleBackAction = () => {
    setOnChangeValidation(true);
    history.goBack();
  };

  const permissionTypes = referenceDataResponse?.value && referenceDataResponse?.value['DAMP_PERMISSION_TYPE'];
  const reasonsForTest = referenceDataResponse?.value && referenceDataResponse?.value['DAMP_TEST_REASON'];
  const ageRanges = referenceDataResponse?.value && referenceDataResponse?.value['AGE_RANGE'];
  const ssaaType = referenceDataResponse?.value && referenceDataResponse?.value['SSAA_TYPE'];
  const australiaStates = referenceDataResponse?.value && referenceDataResponse?.value['AUSTRALIAN_STATES'];
  const stateRegions = referenceDataResponse?.value && referenceDataResponse?.value['STATE_REGIONS'];
  const subjectPositiveToList = referenceDataResponse?.value && referenceDataResponse?.value['SUBSTANCE_SUBJECT_POSITIVE_TO'];
  const testingMethods = referenceDataResponse?.value && referenceDataResponse?.value['TESTING_METHOD'];
  const alcoholReadingRanges = referenceDataResponse?.value && referenceDataResponse?.value['ALCOHOL_READING'];

  if ((!permissionTypes || permissionTypes.length <= 0)
    || (!reasonsForTest || reasonsForTest.length <= 0)
    || (!ageRanges || ageRanges.length <= 0)
    || (!ssaaType || ssaaType.length <= 0)
    || (!australiaStates || australiaStates.length <= 0)
    || (!stateRegions || stateRegions.length <= 0)
    || (!subjectPositiveToList || subjectPositiveToList.length <= 0)
    || (!alcoholReadingRanges || alcoholReadingRanges.length <= 0)
    || (!testingMethods || testingMethods.length <= 0)) {
    return <Loader message='Working. Please wait.....' />;
  }

  const selectedState = getFieldMeta<string>('state').value;
  const filterRegions = stateRegions.filter(f => f.casa_code.startsWith(selectedState));
  const selectedPositiveTo = getFieldMeta('positiveTo').value;

  const handleNext = async () => {
    const validationErrors = await validateForm();
    if (!validationErrors || isEmpty(validationErrors)) {
      setFormAlert(null);
      setOnChangeValidation(false);
      openStepById('summaryAndDeclaration');
    }
    else {
      setFormAlert(DAMP_FORM_VALIDATION_ERROR_ALERT);
      setOnChangeValidation(true);
    }
  };

  return (
    <>
      <ApplicationName name='DAMP Report' />
      <AuDirectionLink
        onClick={handleBackAction}
        text="Back"
        direction="left" />
      <StepContainer hidePreviousLink >
        <AuSelect
          name={'permissionType'}
          label='Permission type'
          block
          options={[
            {
              text: 'Please select',
              value: ''
            },
            ...permissionTypes.map((reason: { casa_name: string; casa_code: string; }) => {
              return {
                text: reason.casa_name,
                value: reason.casa_code
              };
            })
          ]}
        />
        <AuDateInput
          name='dateOfTest'
          label='Date of test'
          hintText='The date when the test was conducted' />
        <AuSelect
          name={'reasonForTest'}
          label='Reason for test'
          block
          options={[
            {
              text: 'Please select',
              value: ''
            },
            ...reasonsForTest.map((reason: { casa_name: string; casa_code: string; }) => {
              return {
                text: reason.casa_name,
                value: reason.casa_code
              };
            })
          ]}
        />
        <AuSelect
          name={'ageRange'}
          label='Age range'
          block
          options={[
            {
              text: 'Please select',
              value: ''
            },
            ...ageRanges.map((range: { casa_name: string; casa_code: string; }) => {
              return {
                text: range.casa_name,
                value: range.casa_code
              };
            })
          ]}
        />
        <AuSelect
          name={'ssaaType'}
          label='SSAA type'
          block
          options={[
            {
              text: 'Please select',
              value: ''
            },
            ...ssaaType.map((range: { casa_name: string; casa_code: string; }) => {
              return {
                text: range.casa_name,
                value: range.casa_code
              };
            })
          ]}
        />
        <AuSelect
          name={'state'}
          label='State'
          block
          options={[
            ...australiaStates.map((range: { casa_name: string; casa_code: string; }) => {
              return {
                text: range.casa_name,
                value: range.casa_code
              };
            })
          ]}
        />
        <AuSelect
          name={'region'}
          label='Region'
          block
          options={[
            {
              text: 'Please select',
              value: ''
            },
            ...filterRegions.map((range: { casa_name: string; casa_code: string; }) => {
              return {
                text: range.casa_name,
                value: range.casa_code
              };
            })
          ]}
        />
        <AuSelect
          name={'positiveTo'}
          label='Positive to'
          block
          options={[
            {
              text: 'Please select',
              value: ''
            },
            ...subjectPositiveToList.map((range: { casa_name: string; casa_code: string; }) => {
              return {
                text: range.casa_name,
                value: range.casa_code
              };
            })
          ]}
        />
        {selectedPositiveTo === 'ALCOHOL' && <AuSelect
          name={'alcoholReading'}
          label='Alcohol reading'
          block
          options={[
            {
              text: 'Please select',
              value: ''
            },
            ...alcoholReadingRanges.map((range: { casa_name: string; casa_code: string; }) => {
              return {
                text: range.casa_name,
                value: range.casa_code
              };
            })
          ]}
        />}
        <AuSelect
          name={'methodOfTesting'}
          label='Method of testing'
          block
          options={[
            {
              text: 'Please select',
              value: ''
            },
            ...testingMethods.map((range: { casa_name: string; casa_code: string; }) => {
              return {
                text: range.casa_name,
                value: range.casa_code
              };
            })
          ]}
        />
        <FormNavigation
          nextButtonText='Next'
          onNextAction={handleNext}
          hidePrevious
          showSaveAndExit={false} />
      </StepContainer>
    </>
  );
};

export default DampReportDetails;